import { api } from '../lib/api.js';
import { APEX_WORDMARK, applyBranding } from '../lib/brand.js';
import { navigate } from '../lib/router.js';
import { isEmbedded } from '../lib/embed.js';

/**
 * Staff sign-in. If already authenticated (incl. the dev-auth shim), forwards
 * straight to the dashboard. Otherwise the Sign-in button hands off to the
 * Keycloak BFF (/auth/login).
 *
 * Visually this is Apex-owned chrome, so it is deliberately locked to the
 * brand-kit monochrome palette (black/white/neutrals) and never picks up the
 * white-label --brand-* colors an agency may have set.
 */
export async function renderLogin(root: HTMLElement): Promise<void> {
  applyBranding(null);
  try {
    const me = await api.get<{ authenticated: boolean }>('/auth/me');
    if (me.authenticated) { navigate('/'); return; }
  } catch { /* show the sign-in panel */ }

  if (isEmbedded()) { renderEmbeddedSignIn(root); return; }

  root.innerHTML = `
  <div class="login-split apex-chrome">
    <aside class="login-brand">
      <div class="login-brand-main">
        <span class="login-wordmark">${APEX_WORDMARK}</span>
        <p class="login-product">Harbor</p>
        <p class="login-tagline">Group benefits operations for agencies.</p>
      </div>
      <p class="login-brand-foot">&copy; Apex Pro</p>
    </aside>

    <main class="login-panel">
      <div class="login-form">
        <h1>Sign in</h1>
        <p class="login-sub">Staff access to Apex Harbor.</p>
        <a class="btn block" href="/auth/login?return=/">Sign in</a>
        <p class="login-foot">Single sign-on · Apex Harbor</p>
      </div>
    </main>
  </div>`;
}

/* --------------------------------------------------------------------------
 * Embedded (GoHighLevel iframe) sign-in
 * ----------------------------------------------------------------------- */

const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 3 * 60 * 1000;

/**
 * Sign-in inside the GHL frame.
 *
 * The ordinary flow navigates to /auth/login, which redirects to Keycloak.
 * Keycloak sends X-Frame-Options: SAMEORIGIN on its login pages, so doing that
 * in the frame renders an empty box with no visible cause. Sign-in therefore
 * happens in a real top-level window instead.
 *
 * Nothing has to be handed back from that window. harbor.apexpro.ai and the GHL
 * white-label host share a registrable domain, so the frame is same-site and the
 * session cookies written by the popup are the same ones the frame already
 * sends — polling /auth/me is enough to notice, and no postMessage handoff or
 * partitioned-cookie exchange is needed.
 */
function renderEmbeddedSignIn(root: HTMLElement): void {
  root.innerHTML = `
  <div class="embed-signin apex-chrome">
    <span class="login-wordmark">${APEX_WORDMARK}</span>
    <h1>Sign in to Apex Harbor</h1>
    <p class="login-sub">Sign-in opens in a new window, then this panel continues on its own.</p>
    <button class="btn" data-action="embed-signin">Sign in</button>
    <p class="embed-signin-status muted" role="status"></p>
  </div>`;

  const btn = root.querySelector<HTMLButtonElement>('[data-action="embed-signin"]');
  const status = root.querySelector<HTMLElement>('.embed-signin-status');
  if (!btn || !status) return;

  btn.addEventListener('click', () => void startEmbeddedSignIn(btn, status));
}

async function startEmbeddedSignIn(btn: HTMLButtonElement, status: HTMLElement): Promise<void> {
  const popup = window.open('/auth/login?return=/', 'apex-harbor-signin', 'width=520,height=680');
  if (!popup) {
    // Popup blockers are common in an iframe. A plain link in a new tab is not
    // blocked, so fall back to one rather than dead-ending.
    status.innerHTML = 'Your browser blocked the sign-in window. ' +
      '<a href="/auth/login?return=/" target="_blank" rel="noopener">Open sign-in in a new tab</a>, ' +
      'then return here.';
    return;
  }

  btn.disabled = true;
  status.textContent = 'Waiting for sign-in…';

  const signedIn = await waitForSession(popup);
  if (signedIn) {
    try { popup.close(); } catch { /* already gone */ }
    status.textContent = 'Signed in. Loading…';
    navigate('/');
    return;
  }

  btn.disabled = false;
  status.textContent = 'Not signed in yet. Try again when you are ready.';
}

/** Polls /auth/me until a session appears, the window closes, or time runs out. */
async function waitForSession(popup: Window): Promise<boolean> {
  const deadline = Date.now() + POLL_TIMEOUT_MS;

  const authenticated = async (): Promise<boolean> => {
    try {
      const me = await api.get<{ authenticated: boolean }>('/auth/me');
      return me.authenticated;
    } catch {
      return false;
    }
  };

  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
    if (await authenticated()) return true;
    // Checked after the poll, not before: a window closed immediately after a
    // successful callback would otherwise be reported as a failed sign-in.
    if (popup.closed) return authenticated();
  }
  return false;
}
